<template>
  <ConfigLayout current="businesshour" :loading="busy">
    <ConfigPage title="営業時間設定" :gap="24" scrollable>
      <template slot="header">
        <Button :disabled="!isChanged" @click="restore">元に戻す</Button>
        <Button class="is-lead" :disabled="!isChanged || GNAVI_MAINTENANCE" @click="save">保存</Button>
      </template>
      <div v-if="GNAVI_MAINTENANCE" class="maintenance">
        ただいま「ぐるなびネット予約」がメンテナンス中につき、こちらの操作は行えません。
      </div>
      <div class="introduction">
        <p>ランチとディナーの営業時間を設定してください</p>
        <ClockIcon class="icon" />
        <a href="#" @click.prevent="isShowBusinesshourModal = true">一括設定する</a>
      </div>

      <DataTable
        class="businesshours"
        :data="allBusinesshour"
        :minItemHeight="72"
        key-field="day"
        :columns="[
          { key: 'day', name: '', filter: ({ day }) => dayOfWeek(day) },
          { key: 'lunch', name: 'ランチ' },
          { key: 'dinner', name: 'ディナー' },
        ]"
      >
        <template #lunch="{ item }">
          <div class="time">
            <TimeInput
              :value="item.lunch.start"
              @change="change($event, item, 'lunch', 'start')"
              :startHour="6"
              :length="25"
              clearButton
              small
              jaWord
            />
            〜
            <TimeInput
              :value="item.lunch.end"
              @change="change($event, item, 'lunch', 'end')"
              :startHour="6"
              :length="25"
              clearButton
              small
              jaWord
            />
          </div>
        </template>
        <template #dinner="{ item }">
          <div class="time">
            <TimeInput
              :value="item.dinner.start"
              @change="change($event, item, 'dinner', 'start')"
              :startHour="6"
              :length="25"
              clearButton
              small
              jaWord
            />
            〜
            <TimeInput
              :value="item.dinner.end"
              @change="change($event, item, 'dinner', 'end')"
              :startHour="6"
              :length="25"
              clearButton
              small
              jaWord
            />
          </div>
        </template>
      </DataTable>

      <section class="section">
        <p class="title">予約時間枠の設定</p>
        <p class="note">
          予約連携をご利用の場合、リセットタイムを変更された際に予約重複の原因になるため、<br />
          予約サイトのリセットタイムについても一致するようにご設定ください。
        </p>
        <DataTable
          class="detail"
          :data="[allDetail]"
          :minItemHeight="72"
          :columns="[
            { key: 'lunchTimer', name: 'ランチ' },
            { key: 'timer', name: 'ディナー' },
            { key: 'resetTime', name: 'リセットタイム' },
          ]"
        >
          <template #lunchTimer="{ item }">
            <SelectBox class="select-box" v-model.number="item.lunchTimer" :items="timers" />
          </template>
          <template #timer="{ item }">
            <SelectBox class="select-box" v-model.number="item.timer" :items="timers" />
          </template>
          <template #resetTime="{ item }">
            <SelectBox class="select-box" v-model.number="item.resetTime" :items="resetTimes" />
          </template>
        </DataTable>
      </section>

      <section class="section">
        <p class="title">満席率の算出設定</p>
        <DataTable
          class="detail"
          :data="[allDetail]"
          :minItemHeight="72"
          :columns="[
            { key: 'lunchRotation', name: 'ランチ回転数' },
            { key: 'dinnerRotation', name: 'ディナー回転数' },
          ]"
        >
          <template #lunchRotation="{ item }">
            <SelectBox class="select-box" v-model.number="item.lunchRotation" :items="rotations" />
          </template>
          <template #dinnerRotation="{ item }">
            <SelectBox class="select-box" v-model.number="item.dinnerRotation" :items="rotations" />
          </template>
        </DataTable>
      </section>

      <section class="section">
        <p class="title">テーブル一覧表示時間設定</p>
        <DataTable
          class="detail"
          :data="[allDetail]"
          :minItemHeight="72"
          :columns="[
            { key: 'dayDisplayTime', name: '当日' },
            { key: 'otherDisplayTime', name: '当日以外' },
          ]"
        >
          <template #dayDisplayTime="{ item }">
            <SelectBox class="select-box" v-model="item.dayDisplayTime" :items="setDisplayTimes(true)" />
          </template>
          <template #otherDisplayTime="{ item }">
            <SelectBox class="select-box" v-model="item.otherDisplayTime" :items="setDisplayTimes(false)" />
          </template>
        </DataTable>
      </section>
      <BusinesshourModal
        v-if="isShowBusinesshourModal"
        @select="setBusinesshour"
        @close="isShowBusinesshourModal = false"
      />
    </ConfigPage>
  </ConfigLayout>
</template>

<script>
import { mapState } from 'vuex'
import { isEqual } from 'lodash'
import { dayOfWeek } from '@/utils/date'
import ConfigLayout from '@/components/templates/ConfigLayout'
import ConfigPage from '@/components/templates/ConfigPage'
import Button from '@/components/atoms/Button'
import SelectBox from '@/components/atoms/SelectBox'
import { confirmDialog } from '@/components/molecules/Dialog'
import Dialog from '@/components/molecules/ModalDialog'
import DataTable from '@/components/molecules/DataTable'
import TimeInput from '@/components/molecules/TimeInput'
import BusinesshourModal from '@/components/molecules/BusinesshourModal'
import ClockIcon from '@/components/icons/clock'

export default {
  name: 'Store',
  components: {
    ConfigLayout,
    ConfigPage,
    Button,
    DataTable,
    TimeInput,
    SelectBox,
    BusinesshourModal,
    ClockIcon,
  },
  data() {
    return {
      STATIC_PATH,
      GNAVI_MAINTENANCE,
      isShowBusinesshourModal: false,
      dayOfWeek,
    }
  },
  computed: {
    ...mapState({
      originBusinesshour: state => state.businesshour.origin,
      originDetail: state => state.businesshour.originDetail,
      allBusinesshour: state => state.businesshour.data,
      allDetail: state => state.businesshour.dataDetail,
      busy: state => state.businesshour.busy,
    }),
    timers() {
      return new Map([
        [60, '1時間'],
        [90, '1時間半'],
        [120, '2時間'],
        [150, '2時間半'],
        [180, '3時間'],
        [210, '3時間半'],
        [240, '4時間'],
        [270, '4時間半'],
        [300, '5時間'],
      ])
    },
    resetTimes() {
      return new Map([
        [0, '0分'],
        [15, '15分'],
        [30, '30分'],
      ])
    },
    rotations() {
      return new Map([
        [1, '1回転'],
        [2, '2回転'],
        [3, '3回転'],
        [4, '4回転'],
      ])
    },
    isChanged() {
      return !isEqual(this.allBusinesshour, this.originBusinesshour) || !isEqual(this.allDetail, this.originDetail)
    },
  },
  watch: {
    isChanged() {
      this.$store.commit('app/editing', this.isChanged)
    },
  },
  async created() {
    await this.fetch(true)
  },
  methods: {
    async fetch(readonly) {
      const { dispatch } = this.$store
      await dispatch('businesshour/get', { readonly })
      await dispatch('businesshour/getDetail', { readonly })
    },
    restore() {
      this.$store.commit('businesshour/resetStatus')
      this.$store.commit('businesshour/resetStatusDetail')
    },
    async save() {
      if (
        await confirmDialog({
          title: '営業時間設定',
          message: '保存してよろしいですか？',
          okText: '保存',
          cancelText: '戻る',
        })
      ) {
        if ((await this.checkStatus()) && (await this.$store.dispatch('businesshour/save'))) {
          await Dialog.show({
            title: '営業時間設定',
            message: '保存が完了しました',
            okText: '戻る',
          })
          this.fetch(false)
        }
      }
    },
    setDisplayTimes(currentTime) {
      const displayTimes = {}
      if (currentTime) {
        this.$set(displayTimes, '00:00', '現在時刻')
      }
      Array.from(
        { length: 19 },
        (_, i) =>
          (displayTimes[`${(6 + i).toString().padStart(2, '0')}:00`] = `${(6 + i).toString().padStart(2, '0')}:00`),
      )
      return displayTimes
    },
    setBusinesshour(data) {
      this.allBusinesshour.forEach(i => {
        i.lunch.start = data.lunchStart
        i.lunch.end = data.lunchEnd
        i.dinner.start = data.dinnerStart
        i.dinner.end = data.dinnerEnd
      })
      this.isShowBusinesshourModal = false
    },
    change(time, item, type, zone) {
      item[type][zone] = time
      if (!time) {
        item[type].start = time
        item[type].end = time
      }
      if (zone === 'start' && time > item[type].end) item[type].end = time
      if (zone === 'end' && item[type].start > time) item[type].start = time
    },
    async checkStatus() {
      let statusError = []
      let duplicateError = []
      this.allBusinesshour.forEach(i => {
        const week = dayOfWeek(i.day)

        // FROMまたはTOの片方のみ未設定の場合
        if (
          (!i.lunch.start && i.lunch.end) ||
          (i.lunch.start && !i.lunch.end) ||
          (!i.dinner.start && i.dinner.end) ||
          (i.dinner.start && !i.dinner.end)
        ) {
          if (statusError.length) statusError += '・'
          statusError += `${week}${!week.indexOf('祝') ? '日' : '曜日'}`
        }
        // ランチとディナー時刻が重複している場合
        if (i.lunch.end > i.dinner.start) {
          if (duplicateError.length) duplicateError += '・'
          duplicateError += `${week}${!week.indexOf('祝') ? '日' : '曜日'}`
        }
      })
      if (statusError.length || duplicateError.length) {
        await Dialog.show({
          title: 'エラー',
          message: statusError.length
            ? (statusError +=
                '\n営業時間を正しく設定してください。\n未設定の場合は、\n開始・終了時間共に未設定を設定してください。')
            : (duplicateError +=
                '\nランチとディナーの時間が重複しています。\n重複しないように設定を変更してください。'),
          okText: '閉じる',
        })
        return false
      }
      return true
    },
  },
}
</script>

<style lang="postcss" scoped>
.introduction {
  color: var(--very-dark-gray-2);
  display: flex;

  a {
    font-weight: bold;
    margin-left: 8px;
  }

  .icon {
    fill: #666;
    padding-left: 16px;
  }
}

.businesshours {
  >>> .day {
    font-weight: 600;
    justify-content: center;
    min-width: 56px;
    white-space: nowrap;
    width: 56px;
  }

  >>> .lunch,
  >>> .dinner {
    justify-content: center;
    text-align: center;
  }

  .time {
    align-items: center;
    display: grid;
    grid-template-columns: 128px 40px 128px;
  }
}

.section {
  display: grid;
  grid-gap: 8px;
}

.title {
  font-size: 1.17em;
  font-weight: bold;
}

.note {
  font-size: 13px;
}

.maintenance {
  color: var(--pure-red);
  font-weight: 600;
}
.detail {
  min-height: unset;

  >>> thead {
    position: unset;
  }

  >>> td {
    align-items: center;
    justify-content: center;
  }

  .select-box {
    width: 126px;
  }
}
</style>
